import { ColumnDef } from '@tanstack/react-table'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { PiMagnifyingGlass } from 'react-icons/pi'
import { toast } from 'react-toastify'
import {
  ActionsDropdown,
  createEditAction,
  createDeleteAction,
} from '../../../components/Buttons/ActionsDropdown'
import confirm from '../../../components/dialogConfirm'
import { ClientDataTable } from '../../../components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import { IconContainer, Input } from '../../../components/Filters/TableSearch'
import * as CommonS from '../../../components/Tables/CommonTable.styles'
import { DateCell } from '../../../components/Tables/CommonTableCells/DateCell'
import {
  FilterIdentifier,
  SavedViewTableLabels,
} from '../../../models/saved_view'
import { ColumnPreset } from '../../../models/column_preset'
import {
  useGetAllColumnPresets,
  deleteColumnPreset,
} from '../../../services/column_presets_api'
import apiService from '../../../services/api'
import { useSideMenu } from '../context/SideMenuContext'
import { EditColumnPresetModal } from './EditColumnPresetModal'
import * as S from './styles'

export function ColumnPresetsTable() {
  const api = apiService()
  const { setDataTabCount } = useSideMenu()

  const { data: categories } = api.useGetActiveCompanyCategoriesAndTypes()
  const {
    data: rawData,
    isLoading,
    refetch,
    isFetching,
  } = useGetAllColumnPresets()

  const [editModalOpen, setEditModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedPreset, setSelectedPreset] = useState<ColumnPreset | null>(
    null
  )

  // Client-side search: all data is loaded at once (no pagination), so filtering in-memory is sufficient.
  const data = useMemo(() => {
    if (!searchQuery || !rawData) return rawData
    const q = searchQuery.toLowerCase()
    return rawData.filter((item) => item.name?.toLowerCase().includes(q))
  }, [rawData, searchQuery])

  useEffect(() => {
    if (rawData) {
      setDataTabCount('columnPresets', rawData.length)
    }
  }, [rawData, setDataTabCount])

  const handleDelete = useCallback(
    async (presetId: number) => {
      const confirmed = await confirm(
        'Are you sure you want to delete this column preset?',
        'Delete Column Preset',
        'This action cannot be undone.',
        { confirmLabel: 'Delete', buttonVariant: 'danger' }
      )

      if (!confirmed) {
        return
      }

      try {
        await deleteColumnPreset(presetId)
        toast.success('Column preset deleted successfully')
        refetch()
      } catch (err) {
        console.error('Failed to delete column preset:', err)
        toast.error('Failed to delete column preset')
      }
    },
    [refetch]
  )

  const handleEdit = useCallback((preset: ColumnPreset) => {
    setSelectedPreset(preset)
    setEditModalOpen(true)
  }, [])

  const columns = useMemo<ColumnDef<ColumnPreset, any>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'identifier',
        header: 'Table',
        cell: (info) => {
          if (info.getValue().toString().startsWith('companies-')) {
            const id = parseInt(info.getValue().toString().split('-')[1])
            let name = ''
            if (categories) {
              loop: for (const c of categories) {
                for (const s of c.sub_categories) {
                  if (s.id === id) {
                    name = s.name
                    break loop
                  }
                }
              }
            }

            return name
          } else {
            return SavedViewTableLabels[info.getValue() as FilterIdentifier]
          }
        },
      },
      {
        accessorKey: 'shared',
        size: 100,
        header: 'Shared',
        cell: (info) => (info.getValue() ? 'Yes' : 'No'),
      },
      {
        id: 'user__first_name',
        header: 'Created By',
        cell: (info) => {
          return (
            info.row.original?.user?.first_name +
            ' ' +
            info.row.original?.user?.last_name
          )
        },
      },
      {
        accessorKey: 'created',
        header: 'Date Created',
        cell: DateCell,
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 70,
        meta: {
          rightAlign: true,
        },
        cell: ({ row }) => (
          <ActionsDropdown
            actions={[
              createEditAction(() => handleEdit(row.original)),
              createDeleteAction(() => handleDelete(row.original.id)),
            ]}
          />
        ),
      },
    ],
    [categories, handleDelete, handleEdit]
  )

  if (isLoading || !rawData || isFetching) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
        }}
      >
        <Spinner animation="border" />
      </div>
    )
  }

  const descriptionText = `
  Saved column presets from across all tables in First Bite. Private presets are only visible to you.
  `

  return (
    <>
      <DataTableContainer>
        <ClientDataTable
          tableHeader={
            <>
              <S.SettingsTableHeader>
                <S.TableDescriptionText>
                  <OverflownTextTooltip
                    tooltipText={descriptionText}
                    maxLines={4}
                    placement="bottom"
                  >
                    {descriptionText}
                  </OverflownTextTooltip>
                </S.TableDescriptionText>
              </S.SettingsTableHeader>
              <CommonS.SearchContainer>
                <CommonS.SearchContainerFilters>
                  <CommonS.TableSearchContainer>
                    <div style={{ position: 'relative' }}>
                      <IconContainer>
                        <PiMagnifyingGlass />
                      </IconContainer>
                      <Input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search column presets..."
                        className="form-control"
                        style={{ width: '100%' }}
                      />
                    </div>
                  </CommonS.TableSearchContainer>
                </CommonS.SearchContainerFilters>
              </CommonS.SearchContainer>
            </>
          }
          height={'100%'}
          stickyFirstColumn={true}
          stickyLastColumn={true}
          enableRowHover={true}
          enableRightCellAlignment={true}
          data={data || []}
          columns={columns}
        />
      </DataTableContainer>
      <EditColumnPresetModal
        show={editModalOpen}
        handleClose={() => {
          setEditModalOpen(false)
          setSelectedPreset(null)
        }}
        columnPreset={selectedPreset}
        onPresetUpdated={refetch}
      />
    </>
  )
}
